import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Send, Phone } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChatPage = ({ user }) => {
  const { userId } = useParams();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [otherUser, setOtherUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    fetchMessages();
    pollRef.current = setInterval(fetchMessages, 3000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [userId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem('kaplie_token');
      const res = await axios.get(`${API}/messages/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(res.data);
      if (res.data.length > 0) {
        const other = res.data.find(m => m.from_user_id === userId);
        if (other) setOtherUser({ id: userId, name: other.from_user_name });
        else {
          const sent = res.data.find(m => m.to_user_id === userId);
          if (sent) setOtherUser({ id: userId, name: sent.to_user_name });
        }
      }
      if (!otherUser) {
        const searchRes = await axios.get(`${API}/users/search?q=${userId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => null);
      }
    } catch (e) {
      if (loading) toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const token = localStorage.getItem('kaplie_token');
      const res = await axios.post(`${API}/messages/send`, {
        to_user_id: userId,
        text: text.trim()
      }, { headers: { Authorization: `Bearer ${token}` } });
      setMessages(prev => [...prev, res.data]);
      setText('');
      if (!otherUser) setOtherUser({ id: userId, name: res.data.to_user_name });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateStr) => {
    return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDateHeader = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) return 'Today';
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Group messages by date
  const groupedMessages = [];
  let lastDate = '';
  messages.forEach(m => {
    const date = new Date(m.created_at).toDateString();
    if (date !== lastDate) {
      groupedMessages.push({ type: 'date', date: formatDateHeader(m.created_at) });
      lastDate = date;
    }
    groupedMessages.push({ type: 'message', ...m });
  });

  return (
    <div className="h-screen bg-black text-white flex flex-col">
      <div className="fixed inset-0 bg-gradient-to-br from-[#D4AF37]/[0.04] via-black to-violet-900/[0.06] pointer-events-none" />

      {/* Header */}
      <nav className="relative z-10 border-b border-white/10 bg-black/50 backdrop-blur-xl">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/messages">
              <button className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                <ArrowLeft className="w-5 h-5 text-white/60" />
              </button>
            </Link>
            <div className="w-9 h-9 bg-gradient-to-br from-[#D4AF37]/20 to-[#C5A059]/10 rounded-full flex items-center justify-center">
              <span className="text-sm font-bold">{(otherUser?.name || '?').charAt(0).toUpperCase()}</span>
            </div>
            <p className="font-semibold">{otherUser?.name || 'Chat'}</p>
          </div>
          <Link to={`/call/dm-${[user?.id, userId].sort().join('-').slice(0, 20)}`}>
            <button className="p-2 hover:bg-white/10 rounded-lg transition-colors" data-testid="call-from-chat">
              <Phone className="w-5 h-5 text-[#D4AF37]" />
            </button>
          </Link>
        </div>
      </nav>

      {/* Messages */}
      <div className="relative z-10 flex-1 overflow-y-auto px-4 py-4 max-w-3xl mx-auto w-full">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-white/20 border-t-[#D4AF37] rounded-full animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-white/40">No messages yet. Say hello!</p>
          </div>
        ) : (
          groupedMessages.map((item, idx) => {
            if (item.type === 'date') {
              return (
                <div key={`date-${idx}`} className="text-center my-4">
                  <span className="text-xs text-white/30 bg-white/5 rounded-full px-3 py-1">{item.date}</span>
                </div>
              );
            }
            const isMe = item.from_user_id === user?.id;
            return (
              <div key={item.id} className={`flex mb-2 ${isMe ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl ${
                  isMe
                    ? 'bg-[#D4AF37]/15 border border-[#D4AF37]/20 rounded-br-md'
                    : 'bg-white/10 border border-white/10 rounded-bl-md'
                }`}>
                  <p className="text-sm text-white">{item.text}</p>
                  <p className="text-[10px] text-white/30 mt-1 text-right">{formatTime(item.created_at)}</p>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="relative z-10 border-t border-white/10 bg-black/50 backdrop-blur-xl p-4">
        <div className="max-w-3xl mx-auto flex gap-2">
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
            placeholder="Type a message..."
            data-testid="message-input"
            className="flex-1 bg-white/10 border border-white/10 rounded-xl px-4 py-3 text-sm placeholder:text-white/30 focus:outline-none focus:border-[#D4AF37]/50"
          />
          <button
            onClick={sendMessage}
            disabled={!text.trim() || sending}
            data-testid="send-message-btn"
            className="p-3 bg-gradient-to-r from-[#D4AF37] to-[#C5A059] hover:shadow-[0_0_20px_rgba(212,175,55,0.3)] text-black rounded-xl transition-colors disabled:opacity-50"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatPage;
